import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import Select from 'components/FormUtils/Select'
import TimePicker from 'components/FormUtils/TimePicker'
import FbButton from 'components/FbUI/FbButton'
import { handleFormError } from 'utils/handleFormError'

import {
  useGetCampaignSchedule,
  useUpdateCampaignSchedule,
} from '../campaign_schedule_api'
import { TIMEZONE_OPTIONS } from './timezones'

interface SequenceScheduleProps {
  campaignId: number
}

interface ScheduleForm {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
  start_time: string // HH:MM format for TimePicker
  end_time: string
  timezone: string
}

const DAYS = [
  { key: 'monday' as const, label: 'Mon' },
  { key: 'tuesday' as const, label: 'Tue' },
  { key: 'wednesday' as const, label: 'Wed' },
  { key: 'thursday' as const, label: 'Thu' },
  { key: 'friday' as const, label: 'Fri' },
  { key: 'saturday' as const, label: 'Sat' },
  { key: 'sunday' as const, label: 'Sun' },
]

const defaultValues: ScheduleForm = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  start_time: '08:00',
  end_time: '17:00',
  timezone: 'America/New_York',
}

// Convert HH:MM:SS to HH:MM for TimePicker
function toTimePickerFormat(time: string): string {
  if (!time) return '08:00'
  // If already HH:MM, return as is
  if (/^\d{2}:\d{2}$/.test(time)) return time
  // If HH:MM:SS, strip seconds
  const match = time.match(/^(\d{2}:\d{2})/)
  return match ? match[1] : '08:00'
}

export function SequenceSchedule({ campaignId }: SequenceScheduleProps) {
  const { data: schedule, isLoading } = useGetCampaignSchedule(campaignId)
  const updateScheduleMutation = useUpdateCampaignSchedule(campaignId)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    setError,
    reset,
  } = useForm<ScheduleForm>({
    defaultValues,
    mode: 'onChange',
  })

  // Reset form when schedule loads
  useEffect(() => {
    if (schedule) {
      reset({
        monday: schedule.monday,
        tuesday: schedule.tuesday,
        wednesday: schedule.wednesday,
        thursday: schedule.thursday,
        friday: schedule.friday,
        saturday: schedule.saturday,
        sunday: schedule.sunday,
        start_time: toTimePickerFormat(schedule.start_time),
        end_time: toTimePickerFormat(schedule.end_time),
        timezone: schedule.timezone,
      })
    }
  }, [schedule, reset])

  const onSubmit = handleSubmit(async (values) => {
    const [err] = await to(updateScheduleMutation.mutateAsync(values))

    if (err) {
      handleFormError(err, setError)
      return
    }

    // Reset form with submitted values to clear dirty state
    reset(values)
    toast.success('Schedule saved successfully')
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    )
  }

  const handleCreateSchedule = async () => {
    const [err] = await to(updateScheduleMutation.mutateAsync(defaultValues))
    if (err) {
      toast.error('Failed to create schedule')
    }
  }

  if (!schedule) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No Schedule Configured
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Set up a sending schedule for this campaign.
          </p>
          <FbButton
            onClick={handleCreateSchedule}
            loading={updateScheduleMutation.isPending}
          >
            Create Schedule
          </FbButton>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <form onSubmit={onSubmit} className="flex-1 overflow-y-auto">
        <div className="space-y-8">
          {/* Active Days - Full width with larger toggles */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
              Active Days
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select the days when emails can be sent.
            </p>
            <div className="flex gap-3">
              {DAYS.map(({ key, label }) => (
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  render={({ field }) => (
                    <button
                      type="button"
                      onClick={() => field.onChange(!field.value)}
                      className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-colors min-w-[80px] ${
                        field.value
                          ? 'bg-[#0F5B4B] text-white border-[#0F5B4B]'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  )}
                />
              ))}
            </div>
          </section>

          {/* Sending Window and Timezone - Side by side */}
          <div className="grid grid-cols-2 gap-8">
            {/* Sending Window */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
                Sending Window
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Set the time window when emails can be sent.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-800 mb-1 block">
                    Start Time
                  </label>
                  <Controller
                    name="start_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        value={field.value}
                        onChange={field.onChange}
                        className="w-full"
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-800 mb-1 block">
                    End Time
                  </label>
                  <Controller
                    name="end_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        value={field.value}
                        onChange={field.onChange}
                        className="w-full"
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            {/* Timezone */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
                Timezone
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Select the timezone for the sending window.
              </p>
              <Select
                name="timezone"
                control={control}
                options={TIMEZONE_OPTIONS}
                placeholder="Select timezone"
                className="!mt-0"
                isClearable={false}
              />
            </section>
          </div>
        </div>
      </form>

      {/* Footer with Save Button */}
      <div className="pt-4 border-t border-gray-200 mt-4">
        <FbButton
          type="submit"
          onClick={onSubmit}
          disabled={!isDirty || isSubmitting}
          loading={isSubmitting}
        >
          Save Schedule
        </FbButton>
      </div>
    </div>
  )
}
